import { Octokit } from '@octokit/rest';
import {
  ConfigApi,
  DiscoveryApi,
  FetchApi,
  createApiRef,
} from '@backstage/core-plugin-api';

/**
 * Same id as @roadiehq/backstage-plugin-github-insights so we can override it.
 */
export const githubInsightsApiRef = createApiRef<ProxyGithubInsightsApi>({
  id: 'plugin.github.service',
});

type ProxyGithubInsightsApi = {
  getContent(props: {
    path?: string;
    repo: string;
    owner: string;
    branch?: string;
    hostname?: string;
  }): Promise<{
    content: string;
    media: Record<string, string>;
    links: Record<string, string>;
  }>;
};

const mimeTypeMap: Record<string, string> = {
  svg: 'image/svg+xml',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
};

function mimeTypeLookup(href: string): string | undefined {
  const match = href.match(/\.([a-zA-Z]*)$/);
  const extension = match ? match[1] : undefined;
  return extension ? mimeTypeMap[extension.toLowerCase()] : undefined;
}

function decodeBase64(value: string): string {
  const normalized = value.replace(/\n/g, '');
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(normalized, 'base64').toString('utf8');
  }
  return decodeURIComponent(
    Array.from(atob(normalized), c =>
      `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`,
    ).join(''),
  );
}

/**
 * Fetches README/content via Backstage proxy + GITHUB_TOKEN, avoiding the org
 * OAuth App access restriction that blocks scmAuth user tokens.
 */
export class ProxyGithubInsightsClient implements ProxyGithubInsightsApi {
  constructor(
    private readonly options: {
      configApi: ConfigApi;
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
    },
  ) {}

  private async getOctokit(): Promise<Octokit> {
    const proxyBase = await this.options.discoveryApi.getBaseUrl('proxy');
    const fetchImpl: typeof fetch = (input, init) =>
      this.options.fetchApi.fetch(String(input), init);
    return new Octokit({
      baseUrl: `${proxyBase}/github/api`,
      request: { fetch: fetchImpl },
    });
  }

  async getContent(props: {
    path?: string;
    repo: string;
    owner: string;
    branch?: string;
    hostname?: string;
  }) {
    const { path: customReadmePath, repo, owner, branch } = props;
    const octokit = await this.getOctokit();
    const query = customReadmePath ? `contents/${customReadmePath}` : 'readme';
    const response = await octokit.request(
      `GET /repos/{owner}/{repo}/${query}`,
      {
        owner,
        repo,
        ...(branch && { ref: branch }),
      },
    );
    const content = decodeBase64(response.data.content);
    const media: Record<string, string> = {};
    const mediaLinks = [
      ...content.matchAll(
        /!\[([^\]]*)\]\(([^)]+\.(?:png|jpg|jpeg|gif|webp|svg))\)/gi,
      ),
    ].map(match => match[2]);

    for (const mediaLink of mediaLinks) {
      const mimeType = mimeTypeLookup(mediaLink);
      if (!mimeType || mediaLink.toLowerCase().startsWith('http')) {
        continue;
      }
      try {
        const path = mediaLink.replace(/^\.\//, '');
        const contentResponse = await octokit.request(
          'GET /repos/{owner}/{repo}/contents/{path}',
          {
            owner,
            repo,
            path,
            ...(branch && { ref: branch }),
          },
        );
        if (
          !Array.isArray(contentResponse.data) &&
          contentResponse.data.type === 'file' &&
          contentResponse.data.content
        ) {
          media[mediaLink] = `data:${mimeType};base64,${contentResponse.data.content.replaceAll(
            '\n',
            '',
          )}`;
        }
      } catch {
        // ignore media load failures
      }
    }

    const links: Record<string, string> = {};
    const loadFromBranch = branch || 'main';
    const markdownLinks = [
      ...content.matchAll(/\[([^\[\]]*)\]\((?!https?:\/\/)(.*?)(\.md)\)/gim),
    ].map(match => `${match[2]}${match[3]}`);
    for (const markdownLink of markdownLinks) {
      links[
        markdownLink
      ] = `https://github.com/${owner}/${repo}/blob/${loadFromBranch}/${markdownLink.replace(
        /^\.\//,
        '',
      )}`;
    }

    return { content, media, links };
  }
}
